import type { LegalEntity, Me } from '../../api.ts';
import { can, canInGroup } from '../../auth.tsx';
import { changed } from '../../components/FormModal.tsx';

/**
 * Ficha da Empresa (Dados Mestres): regras sem tela, para testar sem navegador. Cada aba usa a
 * mesma permissão da tela própria daquele cadastro; o servidor continua conferindo tudo.
 */

export const ENTITY_TABS = [
  'details',
  'branches',
  'locations',
  'costCenters',
  'integration',
  'representatives',
] as const;
export type EntityTab = (typeof ENTITY_TABS)[number];

export interface TabAccess {
  id: EntityTab;
  /** Pode criar e alterar nesta aba (sem isso, só consulta). */
  edit: boolean;
}

const anyOf = (me: Me | null | undefined, permissions: string[]) =>
  permissions.some((p) => can(me, p));

/**
 * Abas que a pessoa vê, na ordem da ficha. Sem a permissão de ver, a aba nem aparece (e os dados
 * dela não são pedidos ao servidor). `readOnly`: a tela está aberta só para consulta (FP&A).
 */
export function entityTabs(me: Me | null | undefined, readOnly = false): TabAccess[] {
  // No FP&A a tela é só consulta da empresa, como antes: sem as abas dos outros cadastros.
  if (readOnly) return [{ id: 'details', edit: false }];
  const tabs: TabAccess[] = [
    { id: 'details', edit: !readOnly && canInGroup(me, 'core.legal_entity.manage') },
  ];
  if (can(me, 'core.branch.read'))
    tabs.push({ id: 'branches', edit: !readOnly && can(me, 'core.branch.manage') });
  // Locais: hoje a lista inteira exige a permissão de administrar (como na tela Locais).
  if (can(me, 'core.location.manage')) tabs.push({ id: 'locations', edit: !readOnly });
  if (anyOf(me, ['core.cost_center.read', 'core.cost_center.manage', 'structure.catalog.read']))
    tabs.push({ id: 'costCenters', edit: !readOnly && can(me, 'core.cost_center.manage') });
  if (can(me, 'structure.catalog.read'))
    tabs.push({ id: 'integration', edit: !readOnly && can(me, 'structure.catalog.update') });
  // Procuradores e sócios: só consulta aqui; poderes, documentos e validação ficam na ficha do
  // representante (ADR 0033), que tem as próprias regras de sigilo.
  if (
    anyOf(me, [
      'core.representative.read',
      'core.representative.manage',
      'core.representative.validate',
    ])
  )
    tabs.push({ id: 'representatives', edit: false });
  return tabs;
}

/** A aba pedida no endereço, se a pessoa pode vê-la; senão, Dados cadastrais. */
export function pickTab(tabs: TabAccess[], wanted: unknown): EntityTab {
  return tabs.find((t) => t.id === wanted)?.id ?? 'details';
}

/** Endereço da ficha aberta: `?entity=<id>&tab=<aba>` (a aba Dados cadastrais não aparece). */
export function recordSearch(entityId: string, tab?: EntityTab): string {
  const p = new URLSearchParams({ entity: entityId });
  if (tab && tab !== 'details') p.set('tab', tab);
  return `?${p.toString()}`;
}

/** Ficha pedida no endereço (link direto), ou nenhuma. */
export function readRecordSearch(search: string): { id: string; tab?: string } | null {
  const p = new URLSearchParams(search);
  const id = p.get('entity');
  return id ? { id, tab: p.get('tab') ?? undefined } : null;
}

export type EntityForm = {
  code: string;
  legalName: string;
  tradeName: string;
  kind: string;
  country: string;
  taxId: string;
  functionalCurrency: string;
  taxRegime: string;
  timezone: string;
  defaultLocale: string;
  parentLegalEntityId: string;
  ownershipPct: string;
  isActive: boolean;
};

export const EMPTY_ENTITY: EntityForm = {
  code: '',
  legalName: '',
  tradeName: '',
  kind: 'operating',
  country: 'BR',
  taxId: '',
  functionalCurrency: 'BRL',
  taxRegime: '',
  timezone: 'America/Sao_Paulo',
  defaultLocale: 'pt-BR',
  parentLegalEntityId: '',
  ownershipPct: '',
  isActive: true,
};

export function entityToForm(e: LegalEntity): EntityForm {
  return {
    code: e.code,
    legalName: e.legalName,
    tradeName: e.tradeName ?? '',
    kind: e.kind,
    country: e.country,
    taxId: e.taxId ?? '',
    functionalCurrency: e.functionalCurrency,
    taxRegime: e.taxRegime ?? '',
    timezone: e.timezone,
    defaultLocale: e.defaultLocale,
    parentLegalEntityId: e.parentLegalEntityId ?? '',
    ownershipPct: e.ownershipPct ? String(Number(e.ownershipPct)) : '',
    isActive: e.isActive,
  };
}

/** O mesmo corpo que a tela Empresas sempre enviou. */
export function entityPayload(f: EntityForm) {
  return {
    ...f,
    code: f.code.toUpperCase(),
    country: f.country.toUpperCase(),
    parentLegalEntityId: f.parentLegalEntityId || null,
    ownershipPct: f.ownershipPct || null,
  };
}

/** Algo mudou em relação ao que veio do servidor (ou ao formulário vazio)? */
export const isDirty = changed;

export type EntityFilter = { q: string; status: 'active' | 'inactive' | 'all'; kind: string };

/** Busca da lista de empresas: código, razão social, nome fantasia ou documento fiscal. */
export function filterEntities(list: LegalEntity[], f: EntityFilter): LegalEntity[] {
  const q = f.q.trim().toLowerCase();
  const digits = q.replace(/\D/g, '');
  return list.filter(
    (e) =>
      (f.status === 'all' || e.isActive === (f.status === 'active')) &&
      (!f.kind || e.kind === f.kind) &&
      (!q ||
        e.code.toLowerCase().includes(q) ||
        e.legalName.toLowerCase().includes(q) ||
        (e.tradeName ?? '').toLowerCase().includes(q) ||
        (!!digits && (e.taxId ?? '').replace(/\D/g, '').includes(digits))),
  );
}
